/*
  The small map on a gallery tile: the item's battery limit, its components inside it, tie-in marks on the
  line, stubs for Connections and links outside it, and the colleagues' faint proposals as dashed shapes.
  It is drawn from the item snapshot (2.9.1). This file only works out where things go; the drawing is in
  `parts/ItemMiniMap.tsx`.
*/

import type { ElementState, Id, ItemSnapshot } from "../../../api/types";
import { displayState, type DisplayState } from "../../../ui/state";

export const MINI_WIDTH = 160;
export const MINI_HEIGHT = 104;
/** The most shapes a tile draws; the rest is counted in words. */
export const MINI_LIMIT = 24;

export interface MiniRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface MiniComponent extends MiniRect {
  id: Id;
  name: string;
  state: DisplayState | null;
  tieIn: boolean;
  inFocus: boolean;
}

export interface MiniStub {
  kind: "connection" | "link";
  name: string;
  /** The stub is a Connection not worked yet: "open, own session". */
  open: boolean;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface MiniMapLayout {
  width: number;
  height: number;
  limit: MiniRect;
  components: MiniComponent[];
  tieIns: Array<{ componentId: Id; x: number; y: number }>;
  stubs: MiniStub[];
  faint: Array<MiniRect & { cardId: string; name: string }>;
  /** Components and proposals that did not fit. */
  more: number;
}

const LIMIT: MiniRect = { x: 26, y: 8, w: MINI_WIDTH - 52, h: MINI_HEIGHT - 16 };
const PAD = 7;
const GAP = 4;
const STUB_LENGTH = 20;

function grid(count: number): { cols: number; rows: number } {
  if (count <= 0) return { cols: 0, rows: 0 };
  const inner = { w: LIMIT.w - PAD * 2, h: LIMIT.h - PAD * 2 };
  const cols = Math.max(1, Math.min(count, Math.round(Math.sqrt((count * inner.w) / inner.h))));
  return { cols, rows: Math.ceil(count / cols) };
}

/** Where the shapes of a snapshot go on a tile. */
export function layoutMiniMap(snapshot: ItemSnapshot): MiniMapLayout {
  const comps = snapshot.components.slice(0, MINI_LIMIT);
  const room = Math.max(0, MINI_LIMIT - comps.length);
  const faintInput = snapshot.faint_proposals.slice(0, room);
  const shown = comps.length + faintInput.length;
  const more = snapshot.components.length + snapshot.faint_proposals.length - shown;

  const { cols, rows } = grid(shown);
  const cellW = cols > 0 ? (LIMIT.w - PAD * 2 - GAP * (cols - 1)) / cols : 0;
  const cellH = rows > 0 ? (LIMIT.h - PAD * 2 - GAP * (rows - 1)) / rows : 0;
  const cell = (index: number): MiniRect => ({
    x: LIMIT.x + PAD + (index % cols) * (cellW + GAP),
    y: LIMIT.y + PAD + Math.floor(index / cols) * (cellH + GAP),
    w: cellW,
    h: cellH,
  });

  const tied = new Set(snapshot.tie_in_marks.map((mark) => mark.component_id));
  const components: MiniComponent[] = comps.map((c, index) => ({
    ...cell(index),
    id: c.element.id,
    name: c.element.name,
    state: displayState((c.element.state ?? null) as ElementState | null, { origin: c.element.origin }),
    tieIn: tied.has(c.element.id),
    inFocus: c.in_focus,
  }));
  const faint = faintInput.map((p, index) => ({ ...cell(comps.length + index), cardId: p.card_id, name: p.name }));

  // A tie-in mark sits on the battery limit, on the edge nearest the component.
  const tieIns = components
    .filter((c) => c.tieIn)
    .map((c) => {
      const cx = c.x + c.w / 2;
      const cy = c.y + c.h / 2;
      const edges = [
        { d: cx - LIMIT.x, x: LIMIT.x, y: cy },
        { d: LIMIT.x + LIMIT.w - cx, x: LIMIT.x + LIMIT.w, y: cy },
        { d: cy - LIMIT.y, x: cx, y: LIMIT.y },
        { d: LIMIT.y + LIMIT.h - cy, x: cx, y: LIMIT.y + LIMIT.h },
      ];
      const nearest = edges.reduce((best, edge) => (edge.d < best.d ? edge : best));
      return { componentId: c.id, x: nearest.x, y: nearest.y };
    });

  // Stubs run out of the limit: on the side of the component they tie in at, or evenly spread.
  const byComponent = new Map(components.map((c) => [c.id, c] as const));
  const placed: Array<{ stub: ItemSnapshot["stubs"][number]; side: "left" | "right"; y: number | null }> = snapshot.stubs.map((stub, index) => {
    const at = stub.at_component_id ? byComponent.get(stub.at_component_id) : undefined;
    if (at) return { stub, side: at.x + at.w / 2 < LIMIT.x + LIMIT.w / 2 ? "left" : "right", y: at.y + at.h / 2 };
    return { stub, side: index % 2 === 0 ? "left" : "right", y: null };
  });
  const stubs: MiniStub[] = [];
  for (const side of ["left", "right"] as const) {
    const onSide = placed.filter((p) => p.side === side);
    const free = onSide.filter((p) => p.y === null);
    onSide.forEach((p) => {
      const slot = p.y ?? LIMIT.y + ((free.indexOf(p) + 1) * LIMIT.h) / (free.length + 1);
      const edgeX = side === "left" ? LIMIT.x : LIMIT.x + LIMIT.w;
      const outX = side === "left" ? LIMIT.x - STUB_LENGTH : LIMIT.x + LIMIT.w + STUB_LENGTH;
      stubs.push({
        kind: p.stub.kind,
        name: p.stub.name,
        open: p.stub.open_own_session,
        x1: edgeX,
        y1: slot,
        x2: outX,
        y2: slot,
      });
    });
  }

  return { width: MINI_WIDTH, height: MINI_HEIGHT, limit: LIMIT, components, tieIns, stubs, faint, more: Math.max(0, more) };
}

/** What a tile's map says in words, for screen readers. */
export function miniMapWords(name: string, snapshot: ItemSnapshot): string {
  const parts = [
    `${snapshot.components.length} ${snapshot.components.length === 1 ? "component" : "components"}`,
    `${snapshot.tie_in_marks.length} tie-in ${snapshot.tie_in_marks.length === 1 ? "point" : "points"}`,
  ];
  if (snapshot.stubs.length > 0) parts.push(`${snapshot.stubs.length} ${snapshot.stubs.length === 1 ? "line or link" : "lines and links"} leaving it`);
  if (snapshot.faint_proposals.length > 0) parts.push(`${snapshot.faint_proposals.length} proposed`);
  return `Map of ${name}: ${parts.join(", ")}`;
}
